import React from 'react';
import { PageHeader, Section } from '../components/PageShell';
import { Button } from '../components/Button';
import {
  Sparkles, Bot, Cpu, Boxes, Package, Users,
  Globe, Smartphone, Cloud, Wrench, LineChart, Zap,
  BarChart3, Paintbrush, Lightbulb, Layers, Code2,
  ArrowUpRight
} from 'lucide-react';

const services = [
  { icon: Sparkles, title: 'Enterprise AI', desc: 'Domain-specific AI systems &mdash; from copilots to intelligent workflows &mdash; built for the enterprise floor.' },
  { icon: Bot, title: 'Generative AI', desc: 'Production-grade GenAI applications with guardrails, evals, observability and cost control.' },
  { icon: Cpu, title: 'AI Agents', desc: 'Autonomous agents that automate real work &mdash; support, ops, sales, engineering &mdash; safely.' },
  { icon: Code2, title: 'Custom Software Development', desc: 'Bespoke platforms and internal tools built for scale, reliability and long-term ownership.' },
  { icon: Boxes, title: 'Enterprise SaaS', desc: 'Multi-tenant SaaS products with billing, SSO, RBAC and compliance built in from day one.' },
  { icon: Package, title: 'ERP Development', desc: 'Modern ERPs tailored to your operating model &mdash; finance, HR, inventory, procurement.' },
  { icon: Users, title: 'CRM Development', desc: 'Sales, service and marketing CRMs that fit your workflow, not the other way around.' },
  { icon: Globe, title: 'Web Development', desc: 'High-performance web apps and marketing sites &mdash; measured in Core Web Vitals, not lorem ipsum.' },
  { icon: Smartphone, title: 'Mobile App Development', desc: 'iOS and Android apps engineered for polish, performance and offline-first flows.' },
  { icon: Cloud, title: 'Cloud Engineering', desc: 'AWS, Azure and GCP done properly &mdash; infra-as-code, multi-region, cost-tuned.' },
  { icon: Wrench, title: 'DevOps', desc: 'CI/CD, Kubernetes, observability and SRE &mdash; so your team ships fast and sleeps well.' },
  { icon: BarChart3, title: 'Business Intelligence', desc: 'From raw data to executive dashboards &mdash; warehouses, semantic layers, self-serve BI.' },
  { icon: Zap, title: 'Automation', desc: 'End-to-end workflow automation &mdash; iPaaS, event-driven pipelines, AI-augmented RPA.' },
  { icon: LineChart, title: 'Data Analytics', desc: 'Modern data stacks that turn silos into decisions across finance, ops and product.' },
  { icon: Paintbrush, title: 'UI/UX Design', desc: 'Enterprise product design and design systems that feel as considered as consumer apps.' },
  { icon: Lightbulb, title: 'IT Consulting', desc: 'Strategy, architecture reviews, and technology due diligence &mdash; senior-only advice.' },
];

export default function Services() {
  return (
    <div data-testid="services-page" className="pb-24">
      <PageHeader
        eyebrow="Services"
        title={<><span className="font-serif">Enterprise-grade</span> software, AI and cloud services.</>}
        subtitle="One senior team across AI, product engineering, cloud and design &mdash; so you don't have to stitch together five vendors."
      >
        <div className="flex flex-wrap gap-3">
          <Button to="/contact" variant="primary">Book a Discovery Call <ArrowUpRight className="w-4 h-4 ml-1" /></Button>
          <Button to="/case-studies" variant="secondary">View Case Studies</Button>
        </div>
      </PageHeader>

      <Section>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map(({ icon: Icon, title, desc }, i) => (
            <div
              key={title}
              data-testid={`service-card-${i}`}
              className="group relative rounded-[28px] bg-white border border-[#7C3AED]/12 p-7 md:p-8 shadow-[0_4px_16px_rgba(0,0,0,0.04)] hover:shadow-[0_10px_40px_rgba(109,40,217,0.18)] hover:border-[#7C3AED]/35 hover:-translate-y-1 transition-all duration-300 flex flex-col"
            >
              <div className="w-11 h-11 rounded-2xl grad-icon text-white flex items-center justify-center shadow-[0_6px_20px_rgba(109,40,217,0.28)] mb-5 group-hover:-rotate-6 transition-transform">
                <Icon className="w-4 h-4" />
              </div>
              <h3 className="font-serif text-2xl text-[#0D212C] mb-3">{title}</h3>
              <p
                className="text-sm text-[#273C46] leading-relaxed mb-6"
                dangerouslySetInnerHTML={{ __html: desc }}
              />
              <a
                href="/contact"
                className="mt-auto inline-flex items-center gap-1 text-sm text-[#051A24] group-hover:underline underline-offset-4"
              >
                Learn more <ArrowUpRight className="w-3.5 h-3.5" />
              </a>
            </div>
          ))}
        </div>
      </Section>

      <Section>
        <div className="rounded-[40px] bg-gradient-to-br from-[#051A24] via-[#1E1B4B] to-[#4C1D95] text-white p-10 md:p-16 shadow-[0_20px_60px_rgba(76,29,149,0.25)] relative overflow-hidden flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div>
            <h2 className="font-serif text-3xl md:text-5xl mb-3">Not sure where to start?</h2>
            <p className="text-[#E0EBF0] max-w-lg">Send us a short brief. We&rsquo;ll come back within one business day with a shaped scope, timeline and team recommendation.</p>
          </div>
          <Button to="/contact" variant="secondary" className="!bg-white !text-[#051A24]">Book a Discovery Call</Button>
        </div>
      </Section>
    </div>
  );
}
